import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

import { FileStatus } from "@cinnamon/design-system/types";
import { type RequestOptions } from "@cinnamon/design-system/utils";
import { API_PATH } from "@constants/config";
import { request } from "@helper/http";
import { downloadFromBinary, getFileNameFromDisposition } from "@helper/utility";

import { PREPROCESSING_WF_UPLOAD_MODE } from "@/constants/workflow";
import { convertFileMetadataToServer, transformJobServerToJob } from "@/converter/file";
import { convertPreprocessingWfToServer } from "@/converter/workflow";
import { invalidatesOn } from "@/helper/invalidatesOn";
import {
  type BatchGetJobsRequest,
  type BatchGetJobsResponse,
  type FileAPIQuery,
  type Job,
  type JobId,
  type JobPreprocessedPdfDownloadServer,
  type JobPreprocessedPdfServer,
  type RerunPayload,
} from "@/types/file";
import { type APIResponse, type ILinecutAnnotationPayLoad } from "@/types/global";
import { type FileMetadata } from "@/types/jobFileUpload";
import { type LLMSample } from "@/types/llmSample";
import { type PreprocessingWorkflow } from "@/types/workflow";

const getContentDisposition = (headers: object): string | undefined => {
  const headerBag = headers as { get?: (name: string) => string | undefined; "content-disposition"?: string };
  const fromGet = headerBag.get?.("content-disposition");
  if (fromGet !== undefined && fromGet !== "") return fromGet;
  const fromIndex = headerBag["content-disposition"];
  return typeof fromIndex === "string" ? fromIndex : undefined;
};

export const getFiles = async (params: FileAPIQuery): Promise<APIResponse<Job>> => {
  const response: APIResponse<Job> = await request({ url: API_PATH.JOBS, method: "get", params });
  const normalizedJobs = response.results.map((item, i) => ({ ...item, number: i + 1 + (params?.offset ?? 0) }));
  return { ...response, results: normalizedJobs };
};

export const batchGetJobs = async (
  payload: BatchGetJobsRequest,
  config?: RequestOptions
): Promise<BatchGetJobsResponse> => {
  return request({
    url: API_PATH.JOBS_BATCH_GET,
    method: "post",
    data: payload,
    ...config,
  });
};

export const onGetNextJobInReview = async (workflowId: string): Promise<APIResponse<Job>> => {
  const payload: FileAPIQuery = {
    workflow: workflowId,
    limit: 1,
    status: FileStatus.NEED_TO_REVIEW,
    ordering: "created_at",
  };
  return getFiles(payload);
};

export const getJob = (jobId: string): Promise<Job> => {
  return request({ url: `${API_PATH.JOBS}${jobId}/`, method: "GET" });
};

const lockJob = (jobId: string): Promise<Job> => {
  return request({ url: API_PATH.LOCK_JOB(jobId), method: "POST" });
};

const unLockJob = (jobId: string): Promise<Job> => {
  return request({ url: API_PATH.LOCK_JOB(jobId), method: "DELETE" });
};

export interface IUploadFilesParams {
  files: File[];
  fileMetadata?: FileMetadata;
  workflowId: string;
  preprocessing?: PreprocessingWorkflow;
}

const convertObjectToArray = (prefix: string, obj?: Record<string, unknown>): [string, string][] | null => {
  if (!obj) return null;
  return Object.entries(obj).map(([key, value]) => [`${prefix}.${key}`, `${value}`]);
};
export async function uploadFiles(params: IUploadFilesParams, config?: RequestOptions): Promise<APIResponse<Job>> {
  const { files, workflowId } = params;
  const formData = new FormData();
  files.forEach((file) => {
    // Same key "file" for all files
    formData.append("file", file);
  });
  formData.append("workflow", workflowId);
  if (params.preprocessing) {
    const preProcessingPayload = convertPreprocessingWfToServer(params.preprocessing);

    if (preProcessingPayload?.upload_mode === PREPROCESSING_WF_UPLOAD_MODE.SPLIT_BY_PAGES) {
      delete preProcessingPayload?.split_pages_per_job;
    }

    // Delete uploadMode from preProcessingPayload
    delete preProcessingPayload?.upload_mode;

    const formDataArray = convertObjectToArray(
      "pre_processors",
      preProcessingPayload as unknown as Record<string, unknown>
    );
    formDataArray?.forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  if (params.fileMetadata) {
    const fileMetadataPayload = convertFileMetadataToServer(params.fileMetadata);
    formData.append("file_metadata", JSON.stringify(fileMetadataPayload));
  }

  return request({
    url: API_PATH.JOBS,
    method: "post",
    data: formData,
    ...config,
  });
}

export async function downloadResult(jobId: JobId): Promise<void> {
  const result = await request({
    url: API_PATH.JOB_DOWNLOAD(jobId),
    method: "get",
    responseType: "blob",
    shouldGetFullResponse: true,
  });
  const fileName = getFileNameFromDisposition(getContentDisposition(result.headers));
  downloadFromBinary(result.data, fileName);
}

export async function downloadResults(ids: JobId[]): Promise<void> {
  const result = await request({
    url: `${API_PATH.JOBS_DOWNLOAD_BATCH}`,
    method: "post",
    responseType: "blob",
    shouldGetFullResponse: true,
    data: { ids },
  });
  const fileName = getFileNameFromDisposition(getContentDisposition(result.headers));
  downloadFromBinary(result.data, fileName);
}

export const getPreprocessedPdfDownload = async (jobId: string): Promise<JobPreprocessedPdfDownloadServer> => {
  return request({ url: API_PATH.JOB_PREPROCESSED_PDF(jobId), method: "get" });
};

export const triggerBuildPreprocessedPdf = async (jobId: string): Promise<JobPreprocessedPdfServer> => {
  return request({ url: API_PATH.JOB_PREPROCESSED_PDF_TRIGGER_BUILD(jobId), method: "post" });
};

export function deleteJob(id: string): Promise<void> {
  return request({
    url: `${API_PATH.JOBS}${id}/`,
    method: "delete",
  });
}

export function bulkDeleteJob(ids: string): Promise<void> {
  return request({
    url: `${API_PATH.BULK_DELETE_JOB}`,
    method: "delete",
    params: { ids },
  });
}
export async function fetchImage(jobId: string): Promise<void> {
  return request({
    url: API_PATH.STATIC(jobId, "parsed"),
    method: "get",
    responseType: "blob",
  });
}

export async function getJobResult(jobId: string): Promise<Job> {
  return new Promise((resolve, reject) => {
    request({
      url: API_PATH.JOB_DATA(jobId),
      method: "get",
      params: {
        show_result_for: "confidence=highest",
      },
    })
      .then((res) => resolve(transformJobServerToJob(res)))
      .catch((error) => reject(error));
  });
}

export async function getJobImages(jobId: string): Promise<APIResponse<string[]>> {
  return request({
    url: `${API_PATH.JOBS}${jobId}/images/?pages=all`,
    method: "get",
  });
}

export async function getJobThumbnails(jobId: string): Promise<APIResponse<string[]>> {
  return request({
    url: `${API_PATH.JOBS}${jobId}/thumbnails/?pages=all`,
    method: "get",
  });
}

export async function updateJobResult(jobId: string, data: unknown): Promise<void> {
  return request({
    url: `${API_PATH.JOBS}${jobId}/result/update/`,
    method: "patch",
    data,
  });
}

export async function updateLineCut({
  jobId,
  annotations,
}: {
  jobId: string;
  annotations: Record<string, ILinecutAnnotationPayLoad>;
}): Promise<void> {
  return request({
    url: `${API_PATH.JOBS}${jobId}/linecut/`,
    method: "patch",
    data: {
      updates: annotations,
    },
  });
}

export interface RerunResponse {
  success: Job[];
  failure: Job[];
}

export const rerunMultipleJobs = (payload: RerunPayload): Promise<RerunResponse> => {
  return request({
    url: `${API_PATH.RERUN_MULTIPLE_ERROR_JOBS}`,
    method: "post",
    data: payload,
  });
};

export const registerJobToLLMSample = (id: string): Promise<LLMSample> => {
  return request({ url: API_PATH.JOB_TO_LLM_SAMPLE(id), method: "POST" });
};

export interface UpdateJobResultMutationParams {
  jobId: JobId;
  payload: unknown;
}

export const jobApi = createApi({
  reducerPath: "jobApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Jobs", "JobOriginalImageDownload"],
  keepUnusedDataFor: 0,
  endpoints: (build) => ({
    getJobs: build.query({
      queryFn: async (payload: FileAPIQuery) => {
        try {
          const data = await getFiles(payload);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["Jobs"],
    }),
    isEmptyWorkflow: build.query({
      queryFn: async (workflow?: string) => {
        try {
          const response = await getFiles({ workflow, limit: 1 });
          return { data: !response.results.length };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["Jobs"],
    }),
    rerunMultipleJobs: build.mutation({
      queryFn: async (payload: RerunPayload) => {
        try {
          const data = await rerunMultipleJobs(payload);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Jobs"], error: [] }),
    }),
    deleteJob: build.mutation({
      queryFn: async (id: JobId) => {
        try {
          const data = await deleteJob(id);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Jobs"], error: [] }),
    }),
    bulkDeleteJob: build.mutation({
      queryFn: async (ids: string) => {
        try {
          const data = await bulkDeleteJob(ids);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Jobs"], error: [] }),
    }),
    updateJobResult: build.mutation({
      queryFn: async (params: UpdateJobResultMutationParams) => {
        try {
          const { jobId, payload } = params;
          const data = await updateJobResult(jobId, payload);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Jobs"], error: [] }),
    }),
    registerJobToLLMSample: build.mutation({
      queryFn: async (id: JobId) => {
        try {
          const data = await registerJobToLLMSample(id);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Jobs"], error: [] }),
    }),
    lockJob: build.mutation({
      queryFn: async (id: JobId) => {
        try {
          const data = await lockJob(id);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: invalidatesOn({ success: ["Jobs"], error: [] }),
    }),
    unLockJob: build.mutation({
      queryFn: async (id: JobId) => {
        try {
          const data = await unLockJob(id);
          return { data };
        } catch (error) {
          return { error };
        }
      },
    }),
    invalidateJobs: build.mutation<null, void>({
      queryFn: () => ({ data: null }),
      invalidatesTags: ["Jobs"],
    }),
    getPreprocessedPdfDownload: build.query({
      queryFn: async ({ jobId }: { jobId: JobId }) => {
        try {
          // Call the api trigger build preprocessed pdf then call get api get preprocessed pdf download
          return { data: await getPreprocessedPdfDownload(jobId) };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_result, _error, { jobId }) => [{ type: "JobOriginalImageDownload", id: jobId }],
    }),
    triggerBuildPreprocessedPdf: build.mutation({
      queryFn: async ({ jobId }: { jobId: JobId }) => {
        try {
          return { data: await triggerBuildPreprocessedPdf(jobId) };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (_result, _error, { jobId }) => [{ type: "JobOriginalImageDownload", id: jobId }],
    }),
  }),
});

export const {
  useGetJobsQuery,
  useIsEmptyWorkflowQuery,
  useRerunMultipleJobsMutation,
  useDeleteJobMutation,
  useBulkDeleteJobMutation,
  useUpdateJobResultMutation,
  useRegisterJobToLLMSampleMutation,
  useLockJobMutation,
  useUnLockJobMutation,
  useInvalidateJobsMutation,
  useGetPreprocessedPdfDownloadQuery,
  useTriggerBuildPreprocessedPdfMutation,
} = jobApi;
